define([
	"dojo/topic",
	"csi/_base/Deferred",
	"csi/_base/underscore",
	"csi/def",
	"csi/html",
	"csi/locale",
	"csi/printUtil",
	"csi/state",
	"csi/state/rootStore",
	"csi/utility/stringUtility",
	"csi/view",

	// load splitters
	"csi/splitters"
], function(topic, Deferred, _, def, html, locale, printUtil, state, {routerStore}, {stringCombine}, csiView) {

	function _breadcrumbText() {
		// summary: return text of breadcrumb trail combined with current view name
		return routerStore.locationTrail;
	}

	function _print(view, domNode, sourceView) {
		var topicHandle = topic.subscribe("/csi/view/preview", function(viewToStart) {
			// find view seen by user that matches that being printed
			var matchingView = sourceView && csiView.getView(viewToStart.defView.getId(), viewToStart.urlRoot, viewToStart.secondaryViewId, sourceView);
			if (matchingView) {
				// provide reference to source view in print view
				if (!matchingView.cannotReuseStoreForPrint) {
					viewToStart.printSourceView = matchingView;
				}

				// apply custom view options to print view - for ad-hoc print, the custom view is not reloaded;
				// instead the options are set here
				var cv = matchingView.prefClass ? matchingView.getSelectedCustomView() : null;
				if (cv) {
					_.each(cv.customOptions, function(opt) {
						viewToStart.set(opt.name, matchingView.get(opt.name));
					});

					viewToStart.onViewPreferences();
				}

				// apply runtime view options to print view
				_.each(matchingView.getPrintProperties(), function(value, key) {
					viewToStart.set(key, value);
				});
			}

			if (viewToStart == view) {
				var paperSize = view.getPrintPaper();
				var orientation = view.getPrintOrientation();
				var autoScale = view.get("printAutoScale");
				var showComments = view.get("printComments");
				var showHeader = view.get("printHeader");
				var breadcrumb = showHeader ? _breadcrumbText() : null;
				var viewName = showHeader ? view.defView.getName() : null;

				printUtil.setPrintOptions(paperSize, orientation, autoScale, showComments, breadcrumb, viewName, (view.splitterType == "datasheet"));
			}
		});

		var dfd = new Deferred().resolve();

		// render view
		dfd = dfd.then(function() {
			return view.placeAt(domNode).startup();
		});

		// remove handle listening for view to start
		dfd = dfd.then(function() {
			topicHandle.remove();
			view.onViewPrint();
		});

		// finish up and return print content
		return dfd.then(
			function() {
				return printUtil.releasePrintArea();
			},
			function(err) {
				html.setPrintStatus(false);
				state.set("print", false);
				throw err || new Error(locale.resource("ErrorPrint"));
			}
		);
	}

	/**
	 * Return title of print page.
	 * @param {Widget} view - The view to print.
	 * @param {boolean} [bPdf=false] - Indicates if printing directly to PDF as opposed to manual printing in browser.
	 * @return {Deferred} Deferred object that will resolve to Object containing the HTML content, as well as paper size and orientation used: {content: String, orientation: String, paper: String}
	 */
	function getPrintTitle(view) {
		// summary: return text of breadcrumb trail combined with current view name
		return stringCombine(_breadcrumbText(), view.defView.getName(), " - ", view.defView.getId() !== def.getHostViewId());
	}

	/**
	 * Render current view for printing and return Deferred object that will resolve to the HTML content to print.
	 * @param {Widget} view - The view to print
	 * @param {boolean} [bPdf=false] - Indicates if printing directly to PDF as opposed to manual printing in browser
	 * @param {boolean} [bPersistent=false] - Indicates if printing content in place as opposed to opening up a separate window
	 * @return {Deferred} Deferred object that will resolve to Object containing the HTML content, as well as paper size and orientation used: {content: String, orientation: String, paper: String}
	 */
	function printCurrentView(view, bPdf, bPersistent) {
		var domNode = printUtil.reservePrintArea(bPdf, bPersistent);
		if (!domNode) {
			return null;
		}

		var defView = view.createViewDefinition();
		var params = {
			printCurrentView: true,
			urlDefaultView: view.get("customViewId")
		};

		if (!view.bPrintResetCache) {
			params.cacheView = view.cacheView;
		}

		var printView = csiView.newView(defView, view.getViewNode(), params);

		return _print(printView, domNode, view);
	}

	/**
	 * Render view for given node for printing and return Deferred object that will resolve to the HTML content to print.
	 * @param {Node} node - The node owning the view to print.
	 * @param {String} viewId - The id of the view to print.
	 * @param {Object} [viewParams] - Parameters to apply to the view.
	 * @param {boolean} [bPdf=false] - Indicates if printing directly to PDF as opposed to manual printing in browser.
	 * @return {Deferred} Deferred object that will resolve to Object containing the HTML content, as well as paper size and orientation used: {content: String, orientation: String, paper: String}
	 */
	function printViewOnNode(node, viewId, viewParams, bPdf) {
		var domNode = printUtil.reservePrintArea(bPdf);
		if (!domNode) return null;

		var defView = def.getNodeViewDef(node, viewId);

		return csiView.newViewDeferred(defView, node, viewParams).then(function(view) {
			return _print(view, domNode);
		});
	}

	return {
		getPrintTitle,
		printCurrentView,
		printViewOnNode
	};
});
